<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { capitalize } from 'lodash';
import { useRenderStatusComposable } from '@/components/modal/useRenderStatusComposable';
import { useConvertTextComposable } from '@/components/modal/useConvertTextComposable';
import { useViewComposable } from '@/views/composable';
import { useDialog } from 'primevue/usedialog';
import AutoComplete from 'primevue/autocomplete';
import DataTable from 'primevue/datatable';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import TableColumn from 'primevue/column';
import SeeMoreToursDescription from './SeeMoreToursDescription.vue';

interface Props {
  option: object;
}
defineProps<Props>();

const { t } = useI18n({ useScope: 'global' });
const { apiOrder } = useViewComposable();
const { handleReplaceValueToName } = useConvertTextComposable();
const { renderClass, renderStatus } = useRenderStatusComposable();
const currentLanguage = getCurrentLocale();
const dialog = useDialog();
const maxLengthText = 25;
const capitalization = capitalize(currentLanguage);

const columns = ref([
  { field: 'svId', header: t('management.tour.list_tour_detail.sv_id') },
  { field: 'ttsId', header: t('management.tour.list_tour_detail.tts_id') },
  { field: `tourName${capitalization}`, header: t('management.tour.list_tour_detail.tour_name') },
  { field: `tourInclusion${capitalization}`, header: t('management.tour.list_tour_detail.tour_inclusions') },
  { field: `tourDuration${capitalization}`, header: t('management.tour.list_tour_detail.tour_duration') },
  { field: `tourType`, header: t('management.tour.list_tour_detail.tour_type') },
  { field: `guided`, header: t('management.tour.list_tour_detail.guided') },
  { field: `visitedCities${capitalization}`, header: t('management.tour.list_tour_detail.visited_cities') },
  { field: 'imageLink', header: t('management.tour.list_tour_detail.image_link') },
  { field: `tourLink${capitalization}`, header: t('management.tour.list_tour_detail.tour_details_link') },
  { field: `tourDescription${capitalization}`, header: t('management.tour.list_tour_detail.tours_description') },
  { field: 'destinationCode', header: t('management.tour.list_tour_detail.destination_code') },
  { field: 'maxRooms', header: t('management.tour.list_tour_detail.max_rooms') },
  { field: 'quoteDate', header: t('management.tour.list_tour_detail.quote_dates') },
  { field: 'travelDate', header: t('management.tour.list_tour_detail.travel_dates') },
  { field: 'blackoutDate', header: t('management.tour.list_tour_detail.blackout_dates') },
  { field: 'groupTypes', header: t('management.tour.list_tour_detail.group_types') },
  { field: 'tourApiCallOrder', header: t('management.tour.list_tour_detail.tour_api_call_order') },
  { field: 'cacheType', header: t('management.tour.list_tour_detail.cache_type') },
  { field: 'instaQuote', header: t('management.tour.list_tour_detail.insta_quote') },
  { field: 'status', header: t('management.tour.list_tour_detail.status') }
]);

const isUseDataType = (field: string) => {
  if (['quoteDate', 'blackoutDate', 'travelDate'].includes(field)) {
    return 'date';
  }
  return 'text';
};

const isCustomMinWidth = (dataField: string) => {
  return ['quoteDate', 'blackoutDate', 'travelDate'].includes(dataField);
};

const getTourOrderLabel = (apiTourOrder: any, slotPropsValue: any) => {
  return apiTourOrder.find((type: any) => type.value === slotPropsValue)?.label;
};

const handleDisplayTourDate = (listTourDate: any, separator = '\n') => {
  let dateFormat = '';
  if (listTourDate.length) {
    dateFormat = listTourDate.map((item: any) => `${formatDate(item.start, 'dd-LLL-yyyy', true, currentLanguage)} to ${formatDate(item.end, 'dd-LLL-yyyy', true, currentLanguage)}`).join(separator);
  }
  return dateFormat;
};

const openSeeMore = (dataField: string, header: string) => {
  const dialogRef = dialog.open(SeeMoreToursDescription, {
    props: {
      header: `${header}`,
      modal: true,
      style: {
        width: '65vw'
      }
    },
    data: {
      dataField: dataField
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};
</script>
<template>
  <div>
    <div class="tour-log">
      <slot></slot>
      <div class="tour-log-detail">
        <DataTable editMode="row" :value="[option.detail]" dataKey="ttsId" showGridlines>
          <TableColumn
            :show-filter-operator="false"
            :show-add-button="false"
            v-for="column of columns"
            :key="column.field"
            :field="column.field"
            :dataType="isUseDataType(column.field)"
            :class="{
              'm-w-300': column.field === 'groupTypes',
              'm-w-140': isCustomMinWidth(column.field),
              'm-w-150': column.field === 'guided',
              'text-link-style': column.field === 'imageLink' || column.field === `tourLink${capitalization}`,
              'text-description': column.field === `tourDescription${capitalization}`
            }"
          >
            <template #header>
              <span :class="{ 'flex justify-content-center gap-3 align-content-center w-full': true }">
                {{ column.header }}
              </span>
            </template>
            <template #body="{ data, field }">
              <span v-if="field === 'quoteDate' || field === 'blackoutDate' || field === 'travelDate'">
                {{ handleDisplayTourDate(data[field]) }}
              </span>
              <span v-else-if="field === 'tourType' || field === 'guided'">
                {{ handleReplaceValueToName(data[field], field) }}
              </span>
              <span v-else-if="field === 'instaQuote' || field === 'status'">
                <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
              </span>
              <span v-else-if="field === 'groupTypes'">
                <AutoComplete class="mb-1 mt-2 min-w-240" disabled :selectionLimit="200" v-model="data[field]" inputId="inputChip" multiple fluid :typeahead="false" @blur="updateAges(field, slotProps.index, index)" />
              </span>
              <span v-else-if="field === 'tourApiCallOrder'">
                <Select disabled class="table-select w-full" :options="apiOrder" optionLabel="label" optionValue="value" v-model="data[field]">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <div class="overflow-hidden text-overflow-ellipsis">{{ getTourOrderLabel(apiOrder, slotProps.value) }}</div>
                    </div>
                  </template>
                </Select>
              </span>
              <div v-else-if="field === `tourDescription${capitalization}` || field === `tourInclusion${capitalization}`">
                <div class="white-space-nowrap overflow-hidden text-overflow-ellipsis description-width">{{ data[field] }}</div>
                <span v-if="data[field] && data[field].length > maxLengthText" class="view-more">
                  <a class="text-blue-600 font-bold underline cursor-pointer text-left uppercase" @click="openSeeMore(data[field], column.header)">{{ t('common.labels.see_more') }}</a>
                </span>
              </div>
              <span v-else>
                {{ data[field] }}
              </span>
            </template>
          </TableColumn>
        </DataTable>
      </div>
    </div>

    <div class="tour-rule mt-4">
      <div class="mt-2 ml-4">
        <ul>
          <li v-if="option.ruleTourDisplay.tourRule !== undefined" class="text-lg mt-1">
            {{ t('rule.tour_rule.tour_rule') }} <span :class="renderClass(option.ruleTourDisplay.tourRule)">({{ renderStatus(option.ruleTourDisplay.tourRule) }})</span>
          </li>
          <li v-if="option.ruleTourDisplay.maxNumbersOfRooms !== undefined" class="text-lg mt-1">
            {{ t('rule.tour_rule.max_number_of_room') }} <span :class="renderClass(option.ruleTourDisplay.maxNumbersOfRooms)">({{ renderStatus(option.ruleTourDisplay.maxNumbersOfRooms) }})</span>
          </li>
          <li v-if="option.ruleTourDisplay.roomPricing !== undefined" class="text-lg mt-1">
            {{ t('rule.tour_rule.roomPricing') }} <span :class="renderClass(option.ruleTourDisplay.roomPricing)">({{ renderStatus(option.ruleTourDisplay.roomPricing) }})</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.tag-rule {
  padding: 0.8rem 1.2rem;
  font-size: 1rem;
}
:deep() {
  .m-w-300 {
    min-width: 300px;
  }
  .m-w-150 {
    min-width: 150px;
  }
  .m-w-140 {
    min-width: 140px;
  }
  .p-chips {
    width: 100%;
  }
  .p-chips-multiple-container {
    width: 100%;
    gap: 0.1rem;
    min-height: 42px;
  }
  .p-chips-input-token,
  .p-chips-token-icon {
    display: none;
  }
  .min-w-240 {
    min-width: 240px !important;
    width: 100%;
  }
  .text-link-style {
    min-width: 400px;
    word-wrap: break-word;
    word-break: break-all;
    white-space: normal;
  }
  .text-description {
    max-width: 500px;
    min-width: 400px;
    word-wrap: break-word;
    word-break: break-all;
    white-space: normal;
  }
}
</style>
